/**
 * Conversion API - Bildformat-Konvertierung
 * 
 * API-Client für den versionierten Endpoint /api/v1/workspace/convert
 */

import axios from 'axios';
import { getCsrfToken } from '../api-client';

// ===========================================
// TYPES
// ===========================================

export type ImageFormat = 'png' | 'jpeg' | 'bmp' | 'webp' | 'avif';

export interface ConversionResultItem {
  success: boolean;
  filename: string;
  original_filename?: string;
  image_id?: string;
  s3_key?: string;
  image?: string; // Base64-encoded
  original_size?: number;
  converted_size?: number;
  original_format?: string;
  converted_format?: string;
  error?: string;
}

export interface ConversionResponse {
  success: boolean;
  // Single image response
  filename?: string;
  original_filename?: string;
  image_id?: string;
  image?: string;
  original_size?: number;
  converted_size?: number;
  original_format?: string;
  converted_format?: string;
  error?: string;
  // Batch response
  images?: ConversionResultItem[];
  total?: number;
  successful?: number;
  failed?: number;
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Konvertiert Bilder in ein anderes Format.
 * 
 * @param files - Array von Dateien zum Konvertieren (max 10, je max 6MB)
 * @param format - Zielformat (png, jpeg, bmp, webp, avif)
 * @param quality - Optional: Qualität 1-100 (nicht für BMP)
 * @returns Promise mit Konvertierungsergebnis(sen)
 */
export async function convertImages(
  files: File[],
  format: ImageFormat,
  quality?: number
): Promise<ConversionResponse> {
  // Validierung
  if (files.length === 0) {
    throw new Error('Keine Dateien zum Konvertieren');
  }
  
  if (files.length > 10) {
    throw new Error('Maximal 10 Bilder pro Request erlaubt');
  }

  const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`Datei "${file.name}" ist zu groß (max. 6MB)`);
    }
  }

  // FormData erstellen
  const formData = new FormData();
  formData.append('format', format);
  
  if (quality !== undefined && format !== 'bmp') {
    formData.append('quality', String(Math.max(1, Math.min(100, quality))));
  }

  // Bilder hinzufügen
  files.forEach((file) => {
    formData.append('images[]', file);
  });

  // CSRF-Token holen
  const csrfToken = await getCsrfToken();

  // Request senden
  const response = await axios.post<ConversionResponse>(
    '/api/v1/workspace/convert',
    formData,
    {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(csrfToken && { 'X-CSRFToken': csrfToken }),
      },
    }
  );

  return response.data;
}

/**
 * Hilfsfunktion: Base64-Bild als Blob herunterladen
 */
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: mimeType });
}

/**
 * Hilfsfunktion: Konvertiertes Bild herunterladen
 */
export function downloadConvertedImage(
  base64Image: string,
  filename: string,
  format: ImageFormat
): void {
  const mimeTypes: Record<ImageFormat, string> = {
    png: 'image/png',
    jpeg: 'image/jpeg',
    bmp: 'image/bmp',
    webp: 'image/webp',
    avif: 'image/avif',
  };

  const blob = base64ToBlob(base64Image, mimeTypes[format]);
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Cleanup
  URL.revokeObjectURL(url);
}

/**
 * Hilfsfunktion: Alle konvertierten Bilder herunterladen
 */
export function downloadAllConvertedImages(
  results: ConversionResultItem[],
  format: ImageFormat
): void {
  results
    .filter((r) => r.success && r.image)
    .forEach((result, index) => {
      // Verzögert downloaden um Browser nicht zu überlasten
      setTimeout(() => {
        downloadConvertedImage(result.image!, result.filename, format);
      }, index * 150);
    });
}

/**
 * Hilfsfunktion: Dateigröße formatieren
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format-Informationen für UI
 */
export const FORMAT_INFO: Record<ImageFormat, { name: string; info: string }> = {
  png: { name: 'PNG', info: 'Transparenz' },
  jpeg: { name: 'JPEG', info: 'Kompakt' },
  bmp: { name: 'BMP', info: 'Unkomprimiert' },
  webp: { name: 'WebP', info: 'Modern' },
  avif: { name: 'AVIF', info: 'Beste Kompression' },
};

/**
 * Formate die Quality-Einstellung unterstützen
 */
export const FORMATS_WITH_QUALITY: ImageFormat[] = ['png', 'jpeg', 'webp', 'avif'];





